import { Message } from "./Interface";
import { PostMessageConnectorBase } from "./PostMessageConnectorBase";
import { Material } from "../types/CommonTypes";
import { parseMTL } from "./FileHandling";
import Config from "../utils/config";

const PROTOCOL_VERSION = 1;

type InternalMaterialPayload = {
    id?: string;
    name?: string;
    type?: string;
};

type InternalFileDownload = {
    type?: string;
    fileName?: string;
    contentType?: string;
    buffer?: ArrayBuffer;
};

export type File = {
    name: string;
    buffer: ArrayBuffer;
    contentType: string;
    parsed?: any;
}

export class MaterialOpenedMessage extends Message {
    static type = "tm.MaterialOpenedMessage" as const;
    static version = 1 as const;
    material: Material;
    constructor(material: Material) {
        super();
        this.material = material;
    }
}

export class MaterialClosedMessage extends Message {
    static type = "tm.MaterialClosedMessage" as const;
    static version = 1 as const;
    constructor() {
        super();
    }
}

export class MaterialFileExportMessage extends Message {
    static type = "tm.FileExportMessage" as const;
    static version = 1 as const;
    file: File;
    constructor(file: File) {
        super();
        this.file = file;
    }
}

export class TotalMateriaConnector extends PostMessageConnectorBase {

    protected readonly name = "TotalMateriaConnector";
    protected readonly protocolVersion = 1;
    protected readonly advertisedMessageTypes = [
        { type: MaterialOpenedMessage.type, version: MaterialOpenedMessage.version },
        { type: MaterialClosedMessage.type, version: MaterialClosedMessage.version },
        { type: MaterialFileExportMessage.type, version: MaterialFileExportMessage.version },
    ] as const;

    protected handleIncomingData(data: unknown): void {
        const d = data as { type?: string };
        switch (d?.type) {
            case 'material':
                this.handleMaterialOpened(data as InternalMaterialPayload);
                break;
            case 'clearmaterial':
                this.handleMaterialClosed();
                break;
            case 'filedownload':
                this.handleFileDownload(data as InternalFileDownload);
                break;
            default:
                console.debug(`[${this.name}] Received message of unknown type from TM portal`, data);
        }
    }

    private handleMaterialOpened(data: InternalMaterialPayload): void {
        const material: Material = { id: data.id || '', name: data.name };
        if(Config.get('matIdPrefix') && material.id && material.id.startsWith(Config.get('matIdPrefix'))){
            material.id = material.id.substring(Config.get('matIdPrefix').length);
        }
        this.notifyListeners(this.envelope(new MaterialOpenedMessage(material)));
    }

    private handleMaterialClosed(): void {
        this.notifyListeners(this.envelope(new MaterialClosedMessage()));
    }

    private handleFileDownload(data: InternalFileDownload): void {
        if (!data?.buffer) return;
        const contentType = data.contentType || "application/octet-stream";
        let parsed: any = null;
        try {
            parsed = parseMTL(data.buffer);
        } catch (err) {
            parsed = {
                raw: (() => { try { return new TextDecoder('utf-8').decode(data.buffer); } catch (e) { return null; } })(),
                error: (err as any)?.message || String(err),
            };
        }
        const file: File = {
            name: data.fileName || parsed?.name || "unknown",
            buffer: data.buffer,
            contentType,
            parsed,
        };
        this.notifyListeners(this.envelope(new MaterialFileExportMessage(file)));
    }
}